"use client";

import { useState } from "react";
import {
  CipherGrid,
  type CipherMode,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type PlayfairFiller,
  type PlayfairRule,
  type PlayfairSquareKind,
  type PlayfairStep,
  playfairDecode,
  playfairEncode,
} from "@/lib/tool-kit/ciphers/playfair";

const STEP_LIMIT = 40;
const SQUARE_SIZE = 5;
const TWO_LETTERS = /.{1,2}/g;

/** English strings. A translated content file overrides these through `ui`. */
export const PLAYFAIR_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Hide the gold in the tree stump",
  keyword: "Keyword",
  keywordSample: "playfair example",
  keywordHint:
    "Its letters fill the square first, each used once; the rest of the alphabet follows in order.",
  squareKind: "Fitting 26 letters into 25 cells",
  mergeIJ: "I and J share a cell",
  dropQ: "Leave out Q",
  filler: "Filler letter",
  fillerHint:
    "Splits a doubled letter (BALLOON → BA LX LO ON) and pads an odd last letter. When the letter to split is the filler itself, a stand-in is used: Q for X (Z if the square has no Q), and X for Z or Q.",
  pairs: "Write the result in pairs",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  fillerNote:
    "Playfair cannot tell a filler from a real letter, so the X added between doubled letters and at the end stays in the decoded text. Likely fillers are highlighted — delete them where they do not belong.",
  ijNote: "I and J share a cell, so every J comes back as I.",
  droppedQ:
    "one: {count} Q was left out — this square has no Q.|other: {count} Qs were left out — this square has no Q.",
  unpaired:
    "The cipher text has an odd number of letters, so the last one ({letter}) was not decoded. Playfair cipher text always has an even length.",
  doubled:
    "one: {count} pair has the same letter twice, which a Playfair encoder never produces — check the cipher text and the keyword.|other: {count} pairs have the same letter twice, which a Playfair encoder never produces — check the cipher text and the keyword.",
  noLetters: "Type some letters to see the pairs.",
  working: "Key square and pairs",
  squareLabel: "Key square",
  squareHint:
    "Select a pair in the table to see it in the square: its letters are shaded blue and the letters they become amber.",
  ruleRow: "Same row",
  ruleColumn: "Same column",
  ruleRectangle: "Rectangle",
  ruleRowEncode:
    "Same row: each letter is replaced by the one to its right, wrapping to the start of the row.",
  ruleRowDecode:
    "Same row: each letter is replaced by the one to its left, wrapping to the end of the row.",
  ruleColumnEncode:
    "Same column: each letter is replaced by the one below it, wrapping to the top.",
  ruleColumnDecode:
    "Same column: each letter is replaced by the one above it, wrapping to the bottom.",
  ruleRectangleBoth:
    "Anywhere else: the two letters mark opposite corners of a rectangle, and each is replaced by the corner in its own row.",
  prepared: "Prepared message",
  pairColumn: "Pair",
  ruleColumnHeader: "Rule",
  resultColumn: "Result",
  showPair: "Show {pair} in the square",
  filledMark: "filler added",
  stepLimit:
    "one: Showing the first pair.|other: Showing the first {count} pairs.",
} as const;

type Strings = typeof PLAYFAIR_CIPHER_STRINGS;

const RULE_LABEL: Record<PlayfairRule, keyof Strings> = {
  row: "ruleRow",
  column: "ruleColumn",
  rectangle: "ruleRectangle",
};

function pairUp(text: string, inPairs: boolean): string {
  if (!inPairs) {
    return text;
  }
  return text.match(TWO_LETTERS)?.join(" ") ?? "";
}

/** The output, with likely fillers marked. */
function OutputView({
  output,
  marks,
  inPairs,
  placeholder,
}: {
  output: string;
  marks: ReadonlySet<number>;
  inPairs: boolean;
  placeholder: string;
}) {
  if (output === "") {
    return (
      <p className="min-h-[96px] rounded-lg border border-border bg-card p-4 text-muted-foreground text-sm">
        {placeholder}
      </p>
    );
  }
  return (
    <p className="min-h-[96px] whitespace-pre-wrap break-all rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed">
      {[...output].map((letter, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: letters are positional
        <span key={index}>
          {inPairs && index > 0 && index % 2 === 0 ? " " : null}
          {marks.has(index) ? (
            <mark className="rounded bg-amber-200/70 px-0.5 text-foreground dark:bg-amber-400/30">
              {letter}
            </mark>
          ) : (
            letter
          )}
        </span>
      ))}
    </p>
  );
}

function PairsTable({
  steps,
  selected,
  onSelect,
  strings,
}: {
  steps: readonly PlayfairStep[];
  selected: number;
  onSelect: (index: number) => void;
  strings: Strings;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.pairColumn}
            </th>
            <th className={TH} scope="col">
              {strings.ruleColumnHeader}
            </th>
            <th className={TH} scope="col">
              {strings.resultColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {steps.map((step, index) => (
            <tr
              className={index === selected ? "bg-primary/5" : undefined}
              // biome-ignore lint/suspicious/noArrayIndexKey: pairs are positional
              key={index}
            >
              <td className={TD}>
                <button
                  aria-label={t(strings.showPair, { pair: step.input })}
                  aria-pressed={index === selected}
                  className="rounded px-1 font-mono underline decoration-dotted underline-offset-4 hover:text-primary"
                  onClick={() => onSelect(index)}
                  type="button"
                >
                  {step.input}
                </button>
                {step.filled ? (
                  <span className="ml-2 text-muted-foreground text-xs">
                    {strings.filledMark}
                  </span>
                ) : null}
              </td>
              <td className={TD}>{strings[RULE_LABEL[step.rule]]}</td>
              <td className={`${TD} font-mono`}>{step.output}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Working({
  mode,
  square,
  steps,
  strings,
}: {
  mode: CipherMode;
  square: string;
  steps: readonly PlayfairStep[];
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [selected, setSelected] = useState(0);
  const current = selected < steps.length ? selected : 0;
  const step = steps.at(current);
  const tone = (index: number): GridTone | undefined => {
    const letter = square.charAt(index);
    if (step?.input.includes(letter)) {
      return "primary";
    }
    return step?.output.includes(letter) ? "secondary" : undefined;
  };
  const shown = steps.slice(0, STEP_LIMIT);
  const encoding = mode === "encode";
  return (
    <>
      <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm leading-relaxed">
        <li>{encoding ? strings.ruleRowEncode : strings.ruleRowDecode}</li>
        <li>
          {encoding ? strings.ruleColumnEncode : strings.ruleColumnDecode}
        </li>
        <li>{strings.ruleRectangleBoth}</li>
      </ul>
      <div className="space-y-2">
        <CipherGrid
          cells={[...square]}
          columns={SQUARE_SIZE}
          label={strings.squareLabel}
          tone={tone}
        />
        <p className={HINT}>{strings.squareHint}</p>
      </div>
      {steps.length > 0 ? (
        <div className="space-y-2">
          {encoding ? (
            <p className="text-sm">
              <span className={LABEL}>{strings.prepared}: </span>
              <span className="font-mono text-foreground">
                {steps.map((item) => item.input).join(" ")}
              </span>
            </p>
          ) : null}
          <PairsTable
            onSelect={setSelected}
            selected={current}
            steps={shown}
            strings={strings}
          />
          {steps.length > STEP_LIMIT ? (
            <p className={HINT}>
              {tPlural(strings.stepLimit, locale, STEP_LIMIT, {
                count: fmt(STEP_LIMIT),
              })}
            </p>
          ) : null}
        </div>
      ) : (
        <Notice>{strings.noLetters}</Notice>
      )}
    </>
  );
}

export default function PlayfairCipherWidget() {
  const strings = useToolUi(PLAYFAIR_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [kind, setKind] = useState<PlayfairSquareKind>("merge-ij");
  const [filler, setFiller] = useState<PlayfairFiller>("X");
  const [inPairs, setInPairs] = useState(true);

  const options = { keyword, kind, filler };
  const encoded = mode === "encode" ? playfairEncode(input, options) : null;
  const decoded = mode === "decode" ? playfairDecode(input, options) : null;
  const result = encoded ?? decoded;
  const output = result ? result.output : "";
  const marks = new Set(decoded?.likelyFillers ?? []);

  const fillerOptions: Option<PlayfairFiller>[] = (
    kind === "merge-ij" ? (["X", "Z", "Q"] as const) : (["X", "Z"] as const)
  ).map((letter) => ({ value: letter, label: letter }));

  function changeKind(next: PlayfairSquareKind) {
    setKind(next);
    if (next === "drop-q" && filler === "Q") {
      setFiller("X");
    }
  }

  const keyControls = (
    <div className="space-y-4">
      <TextField
        hint={strings.keywordHint}
        id="pf-keyword"
        label={strings.keyword}
        mono
        onChange={setKeyword}
        value={keyword}
      />
      <PillGroup
        label={strings.squareKind}
        name="pf-square"
        onChange={changeKind}
        options={[
          { value: "merge-ij", label: strings.mergeIJ },
          { value: "drop-q", label: strings.dropQ },
        ]}
        value={kind}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.fillerHint}
          id="pf-filler"
          label={strings.filler}
          onChange={setFiller}
          options={fillerOptions}
          value={filler}
        />
        <div className="sm:pt-8">
          <Checkbox
            checked={inPairs}
            label={strings.pairs}
            onChange={setInPairs}
          />
        </div>
      </div>
    </div>
  );

  const droppedQ = result?.droppedQ ?? 0;
  const notices = (
    <>
      {droppedQ > 0 ? (
        <Notice>
          {tPlural(strings.droppedQ, locale, droppedQ, {
            count: fmt(droppedQ),
          })}
        </Notice>
      ) : null}
      {decoded?.unpaired ? (
        <ErrorNotice>
          {t(strings.unpaired, { letter: decoded.unpaired })}
        </ErrorNotice>
      ) : null}
      {decoded && decoded.doubled > 0 ? (
        <ErrorNotice>
          {tPlural(strings.doubled, locale, decoded.doubled, {
            count: fmt(decoded.doubled),
          })}
        </ErrorNotice>
      ) : null}
    </>
  );

  const outputNotes =
    decoded && output !== "" ? (
      <div className="space-y-2">
        <p className={HINT}>{strings.fillerNote}</p>
        {kind === "merge-ij" ? <p className={HINT}>{strings.ijNote}</p> : null}
      </div>
    ) : null;

  return (
    <CipherWorkbench
      idPrefix="pf"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={pairUp(output, inPairs)}
      outputNotes={outputNotes}
      outputView={
        <OutputView
          inPairs={inPairs}
          marks={marks}
          output={output}
          placeholder={strings.outputPlaceholder}
        />
      }
      working={
        result ? (
          <Working
            mode={mode}
            square={result.square}
            steps={result.steps}
            strings={strings}
          />
        ) : null
      }
    />
  );
}
